import { useQuery, UseQueryResult } from '@tanstack/react-query';
import useGetApi from '../useGetApi';
import useMutationApi from '../useMutationApi';
import config from 'src/config';
import ApiResponse from '../ApiResponse';
import ApiError from '../ApiError';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';
import {
  SupplierPayment,
  CreateSupplierPaymentRequest,
  SupplierPaymentDocument,
} from './types';

// Get supplier payments by agreement ID
export const useSupplierPaymentsByAgreement = (
  agreementId: string,
  enabled = true,
) => {
  return useGetApi<SupplierPayment[]>({
    endpoint: `rebates/supplier-payments/agreement/${agreementId}`,
    queryKey: ['supplierPayments', 'byAgreement', agreementId],
    options: {
      enabled: enabled && !!agreementId,
    },
  });
};

// Bulk: Get supplier payments grouped by supplier ids
export const useSupplierPaymentsBySuppliers = (
  supplierIds: string[],
  enabled = true,
): UseQueryResult<
  | Array<{
      supplierId: string;
      payments: SupplierPayment[];
    }>
  | undefined,
  ApiError
> => {
  const ids = Array.from(new Set(supplierIds.filter(Boolean)));

  return useQuery<
    | Array<{
        supplierId: string;
        payments: SupplierPayment[];
      }>
    | undefined,
    ApiError
  >({
    queryKey: ['supplierPayments', 'bySuppliers', ids.sort().join(',')],
    queryFn: async () => {
      if (ids.length === 0) {
        return [];
      }

      const response = await fetch(
        `${config.apiBaseUrl}/rebates/supplier-payments/by-suppliers`,
        {
          method: 'POST',
          headers: buildPosthogHeaders({
            'Content-Type': 'application/json',
          }),
          credentials: 'include',
          body: JSON.stringify({ ids }),
        },
      );

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new Error(errorResponse.message);
      }

      const data: ApiResponse<
        Array<{
          supplierId: string;
          payments: SupplierPayment[];
        }>
      > = await response.json();

      return data.data;
    },
    enabled: enabled && ids.length > 0,
  });
};

// Get supplier payments by date period (finds agreements overlapping period automatically)
export const useSupplierPaymentsByPeriod = (
  startDate: string,
  endDate: string,
  enabled = true,
) => {
  const query = `?startDate=${startDate}&endDate=${endDate}`;
  const endpoint = `rebates/supplier-payments/period${query}`;

  return useGetApi<
    Array<{
      supplierId: string;
      payments: SupplierPayment[];
    }>
  >({
    endpoint,
    options: { enabled: enabled && !!startDate && !!endDate },
  });
};

// Get supplier payment document
export const useSupplierPaymentDocument = (
  paymentId: string,
  enabled = true,
) => {
  return useGetApi<SupplierPaymentDocument>({
    endpoint: `rebates/supplier-payments/${paymentId}/document`,
    options: {
      enabled: enabled && !!paymentId,
    },
  });
};

// Create supplier payment
export const useCreateSupplierPayment = () => {
  return useMutationApi<CreateSupplierPaymentRequest, { id: string }>({
    endpoint: 'rebates/supplier-payments',
    method: 'POST',
    transformRequest: (data: CreateSupplierPaymentRequest) => {
      const formData = new FormData();
      formData.append('date', data.date);
      formData.append('startDate', data.startDate);
      formData.append('endDate', data.endDate);
      if (data.amount !== undefined) {
        formData.append('amount', data.amount.toString());
      }
      formData.append('type', data.type);
      formData.append('rebateAgreementId', data.rebateAgreementId);
      if (data.memo) {
        formData.append('memo', data.memo);
      }
      if (data.attachment) {
        formData.append('attachment', data.attachment);
      }
      // Tax fields
      if (data.grossAmount !== undefined) {
        formData.append('grossAmount', data.grossAmount.toString());
      }
      if (data.taxRate !== undefined) {
        formData.append('taxRate', data.taxRate.toString());
      }
      if (data.taxAmount !== undefined) {
        formData.append('taxAmount', data.taxAmount.toString());
      }
      // Foreign-currency fields
      if (data.originalCurrency !== undefined) {
        formData.append('originalCurrency', data.originalCurrency);
      }
      if (data.originalGrossAmount !== undefined) {
        formData.append(
          'originalGrossAmount',
          data.originalGrossAmount.toString(),
        );
      }
      if (data.originalTaxAmount !== undefined) {
        formData.append('originalTaxAmount', data.originalTaxAmount.toString());
      }
      if (data.originalAmount !== undefined) {
        formData.append('originalAmount', data.originalAmount.toString());
      }
      if (data.conversionRate !== undefined) {
        formData.append('conversionRate', data.conversionRate.toString());
      }
      return formData;
    },
  });
};

// Delete supplier payment
export const useDeleteSupplierPayment = (paymentId: string) => {
  return useMutationApi<string, void>({
    endpoint: `rebates/supplier-payments/${paymentId}`,
    method: 'DELETE',
  });
};
